// Double authentification (TOTP) : QR code à scanner avec une application d'authentification
// (Google Authenticator, Microsoft Authenticator, FreeOTP...), puis un premier code pour l'activer.
import qrcode from "qrcode-generator";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, unwrap } from "../api/client";
import type { Me } from "../api/types";
import { t } from "../lib/i18n";
import { Button, Field, FormError, Modal, TextInput, useToast } from "./ui";

function QrCode({ text }: { text: string }) {
  // dessiné en SVG case par case : net à toute taille, sans HTML injecté
  const { size, path } = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    let d = "";
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
    return { size: n + 8, path: d };
  }, [text]);
  return (
    <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={t("QR code de la double authentification")}
      className="mx-auto size-48 rounded-md" shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

/** Code à 6 chiffres : espaces tolérés (certaines applications affichent « 123 456 »). */
export function CodeInput({ id, value, onChange, autoFocus }: {
  id: string; value: string; onChange: (v: string) => void; autoFocus?: boolean;
}) {
  return (
    <TextInput id={id} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} required
      autoFocus={autoFocus} className="num tracking-[0.3em]" value={value} onChange={(e) => onChange(e.target.value)} />
  );
}

/** Génère un secret, affiche le QR code et active la double authentification au premier code correct. */
export function TotpEnroll({ onEnabled, formId }: { onEnabled: (me: Me) => void; formId: string }) {
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    unwrap(api.POST("/api/auth/totp/setup")).then(setSetup).catch((e: Error) => setError(e.message));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onEnabled(await unwrap(api.POST("/api/auth/totp/enable", { body: { code: code.replace(/\s/g, "") } })));
    } catch (err) {
      setError((err as Error).message);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form id={formId} onSubmit={submit} className="flex flex-col gap-4">
      <ol className="list-decimal space-y-1 pl-5 text-[13px] text-muted">
        <li>{t("Ouvrez une application d'authentification sur votre téléphone (Google Authenticator, Microsoft Authenticator, FreeOTP…).")}</li>
        <li>{t("Ajoutez un compte en scannant ce QR code.")}</li>
        <li>{t("Saisissez le code à 6 chiffres affiché par l'application.")}</li>
      </ol>
      {setup ? (
        <>
          <QrCode text={setup.otpauth_url} />
          <details className="text-[12px] text-muted">
            <summary className="cursor-pointer">{t("Impossible de scanner ? Saisir la clé à la main")}</summary>
            <code className="mt-2 block break-all rounded-md border border-line bg-bg p-2 font-mono text-ink">
              {setup.secret.match(/.{1,4}/g)?.join(" ")}
            </code>
          </details>
        </>
      ) : !error && <p className="text-center text-muted">{t("Préparation du QR code…")}</p>}
      <Field label={t("Code de l'application")} htmlFor={`${formId}-code`}>
        <CodeInput id={`${formId}-code`} value={code} onChange={setCode} />
      </Field>
      <FormError message={error} />
      <Button variant="primary" type="submit" disabled={busy || !setup}>{t("Activer la double authentification")}</Button>
    </form>
  );
}

/** Menu utilisateur : activer, ou désactiver (mot de passe demandé ; impossible pour un administrateur). */
export function TotpModal({ me, onChanged, onClose }: { me: Me; onChanged: (me: Me) => void; onClose: () => void }) {
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const disable = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onChanged(await unwrap(api.POST("/api/auth/totp/disable", { body: { password } })));
      toast(t("Double authentification désactivée"), "warn");
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const footer = <Button variant="ghost" onClick={onClose}>{t("Fermer")}</Button>;
  if (!me.totp_enabled) {
    return (
      <Modal open title={t("Activer la double authentification")} onClose={onClose} footer={footer}>
        <TotpEnroll formId="totp-enroll" onEnabled={(m) => {
          toast(t("Double authentification activée. Vos autres sessions ont été fermées."));
          onChanged(m);
          onClose();
        }} />
      </Modal>
    );
  }
  if (me.role === "admin") {
    return (
      <Modal open title={t("Double authentification")} onClose={onClose} footer={footer}>
        <p className="text-muted">
          {t("Elle est active et obligatoire pour les administrateurs. Téléphone perdu ou changé : un autre administrateur la réinitialise depuis la page Comptes, et vous la réactivez à la connexion suivante.")}
        </p>
      </Modal>
    );
  }
  return (
    <Modal open title={t("Désactiver la double authentification")} onClose={onClose}
      footer={<>
        {footer}
        <Button variant="danger" type="submit" form="totp-disable" disabled={busy}>{t("Désactiver")}</Button>
      </>}>
      <form id="totp-disable" onSubmit={disable} className="flex flex-col gap-4">
        <p className="text-muted">{t("Votre compte ne sera plus protégé que par son mot de passe.")}</p>
        <Field label={t("Mot de passe")} htmlFor="totp-disable-pass">
          <TextInput id="totp-disable-pass" type="password" autoComplete="current-password" required autoFocus
            value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <FormError message={error} />
      </form>
    </Modal>
  );
}
